import React, { useState } from 'react'
import Select from 'react-select'
import useSWR from 'swr'
import { useWidgetSite } from '../../../contexts/WidgetSiteContext'
import { PageTypeInfo, fetchPageTypeConfig } from '../../../services/api'
import LinkRefSelector, {
  EMPTY_LINK_REF,
  linkRefToMetadata,
  metadataToLinkRef,
} from '../LinkRefSelector'
import formStyles from '../../../styles/forms.module.scss'
import selectStyles from '../../../styles/react-select.module.scss'
import type { CarouselMetadata, ShowMoreMode } from './CarouselEditor'

// The "see more" link. Two things are authored: when it shows (mode) and,
// optionally, where it goes. With no destination override the link points at
// this widget's own collection page — ACE substitutes that default and resolves
// it through the same LinkRef machinery, so there is no path to type here and
// none to keep in sync.

interface CarouselShowMoreEditorProps {
  metadata: CarouselMetadata
  onChange: (metadata: CarouselMetadata) => void
}

const MODE_OPTIONS: Array<{ value: ShowMoreMode; label: string }> = [
  { value: 'never', label: 'Never — no link' },
  { value: 'if_more', label: 'When there is more than the rail shows' },
  { value: 'always', label: 'Always' },
]

const CarouselShowMoreEditor: React.FC<CarouselShowMoreEditorProps> = ({ metadata, onChange }) => {
  const { siteId } = useWidgetSite()
  const { data: typeConfig } = useSWR(
    siteId ? ['page-type-config', siteId] : null,
    () => fetchPageTypeConfig(siteId),
    { revalidateOnFocus: false },
  )
  const pageTypes: PageTypeInfo[] = typeConfig?.types || []

  const showMore = metadata.show_more
  const mode = showMore?.mode ?? 'never'
  // Whether the author has opened the override. A saved link implies yes;
  // otherwise it's local state so toggling it off clears the ref.
  const [overriding, setOverriding] = useState(Boolean(showMore?.link))

  const apply = (next: CarouselMetadata['show_more']) => {
    const cleaned = { ...metadata, show_more: next }
    if (!next) delete cleaned.show_more
    onChange(cleaned)
  }

  return (
    <>
      <div className={formStyles.fieldGroup}>
        <label title="When the “see more” link appears on this rail.">Show more</label>
        <Select
          value={MODE_OPTIONS.find(o => o.value === mode) ?? MODE_OPTIONS[0]}
          onChange={(o) => {
            if (!o) return
            // An explicit `never` is worth keeping when a label or override is
            // already set — it turns the link off without losing the wording.
            if (o.value === 'never' && !showMore?.label && !showMore?.link) {
              apply(undefined)
              return
            }
            apply({ ...showMore, mode: o.value })
          }}
          options={MODE_OPTIONS}
          className={selectStyles.reactSelect}
          classNamePrefix="react-select"
        />
      </div>

      {showMore && mode !== 'never' && (
        <>
          <div className={formStyles.fieldGroup}>
            <label>Link label</label>
            <input
              type="text"
              value={showMore.label ?? ''}
              onChange={(e) => apply({ ...showMore, label: e.target.value || undefined })}
              placeholder="See all"
            />
          </div>

          <div className={formStyles.fieldGroup}>
            <label className={formStyles.checkboxLabel}>
              <input
                type="checkbox"
                checked={overriding}
                onChange={(e) => {
                  setOverriding(e.target.checked)
                  if (!e.target.checked) apply({ ...showMore, link: undefined })
                }}
              />
              Send it somewhere other than this carousel’s collection page
            </label>
            {!overriding && (
              <p className={formStyles.hint}>
                Links to this carousel’s own collection page, which lists the same content.
              </p>
            )}
          </div>

          {overriding && (
            <LinkRefSelector
              siteId={siteId}
              value={showMore.link ? metadataToLinkRef(showMore.link) : EMPTY_LINK_REF}
              onChange={(v) =>
                apply({ ...showMore, link: linkRefToMetadata(v, pageTypes) ?? undefined })
              }
            />
          )}
        </>
      )}
    </>
  )
}

export default CarouselShowMoreEditor
